// src/components/Common/charts/chartUtils.js

/**
 * chartUtils.js
 *
 * Centralized utilities for building Chart.js datasets and options used by:
 * - LineChartWithToggles (Line / Bar / Area / Stacked Area)
 * - ProgramBudget and related BoE/Town charts
 *
 * Design goals:
 * - Keep React components “thin” (state + rendering) and move dataset logic here.
 * - Support both “auto grouping” (based on series.group) and explicit group toggle mode.
 * - Provide consistent legend + tooltip behavior, especially for helper/proposed datasets.
 */

/**
 * @typedef {"currency"|"count"|"percent"|"text"} ValueType
 */

/**
 * Supported chart type selector values used by LineChartWithToggles.
 *
 * Notes:
 * - "area" and "stackedArea" are implemented as line charts with `fill` and stacking.
 *
 * @typedef {"line"|"bar"|"stackedBar"|"stackedBar100"|"area"|"stackedArea"} ChartType
 */

/**
 * Standard series definition consumed by the chart builders.
 *
 * @typedef {Object} ChartSeries
 * @property {string} key - Unique identifier used for toggles and dataset metadata.
 * @property {string} label - Display label for legend/toggles/tooltips.
 * @property {number[]|null[]} data - Primary values aligned to `labels`.
 * @property {number[]|null[]} [proposedData] - Optional proposed overlay values aligned to `labels`.
 * @property {string} [group] - Group key for auto-grouping (e.g., "salaries", "materials").
 * @property {boolean} [defaultVisible] - Optional initial visibility hint.
 * @property {ValueType} [valueType="currency"] - Formatting mode.
 * @property {string} [color] - Optional override for the dataset color.
 */

/**
 * Explicit toggle grouping definition used when rendering “group toggles”
 * (e.g., diff charts or “Actual vs Approved” views).
 *
 * @typedef {Object} SeriesToggleGroup
 * @property {string} key - Unique identifier for React rendering.
 * @property {string} label - Checkbox label shown in UI.
 * @property {string} [groupKey="other"] - Used to cluster groups under headings (positions/salaries/...).
 * @property {string[]} seriesKeys - Series keys controlled by this group toggle.
 * @property {boolean} [defaultVisible] - Optional initial visibility hint for getInitialVisibleKeys().
 */

/**
 * When proposedData exists, you can render it as:
 * - "overlay": a second dashed dataset
 * - "merge": one continuous dataset with dashed segment for proposed-only points
 *
 * @typedef {"overlay"|"merge"} ProposedRenderMode
 */

/** Default color palette for datasets (cycled when no color provided). */
export const DEFAULT_COLORS = [
  // --- Primary high-contrast set (first 8) ---
  "#dc2626", // Red
  "#2563eb", // Blue
  "#7c3aed", // Purple
  "#0d9488", // Teal
  "#ca8a04", // Gold / Dark Yellow
  "#db2777", // Magenta
  "#16a34a", // Green (intentionally AFTER first 7)
  "#f97316", // Orange

  // --- Secondary distinct hues (next 8) ---
  "#991b1b", // Dark Red
  "#1e40af", // Dark Blue
  "#ea580c", // Burnt Orange
  "#6b21a8", // Deep Purple
  "#115e59", // Dark Teal
  "#854d0e", // Olive Gold
  "#be185d", // Dark Pink
  "#166534", // Dark Green

  // --- Tertiary / lighter variants (next 8) ---
  "#f87171", // Light Red
  "#60a5fa", // Light Blue
  "#fdba74", // Light Orange
  "#c4b5fd", // Light Purple
  "#5eead4", // Light Teal
  "#fde047", // Yellow (safe late)
  "#f9a8d4", // Light Pink
  "#86efac", // Light Green

  // --- Final fallback contrasts (last 8) ---
  "#7f1d1d", // Very Dark Red
  "#172554", // Navy
  "#9a3412", // Brownish Orange
  "#4c1d95", // Indigo Purple
  "#134e4a", // Teal Black
  "#713f12", // Brown Gold
  "#831843", // Wine
  "#052e16", // Forest Green
];

/**
 * Preferred ordering used when grouping legend/toggles.
 * Unrecognized groups are sorted after these (stable ordering by index fallback).
 */
export const GROUP_ORDER = [
  "revenue",
  "revenues",
  "taxLevy",
  "motorVehicle",
  "delinquentFees",
  "permits",
  "townClerk",
  "planning",
  "publicWorks",
  "parksRecreation",
  "healthSafety",
  "communityServices",
  "education",
  "purchasing",
  "publicSafety",
  "library",
  "reimbursements",
  "investmentIncome",
  "leaseProperty",
  "miscellaneous",
  "spending",
  "budget",
  "budgets",
  "totals",
  "district",
  "districts",
  "elementary",
  "rent",
  "income",
  "housing",
  "fmr",
  "limits",
  "staffTotals",
  "staffAdministration",
  "staffCertified",
  "staffInstructionalSupport",
  "staffSubstitutes",
  "staffOperations",
  "staffClerical",
  "staffOther",
  "positions",
  "salaries",
  "salary",
  "services",
  "purchased Services",
  "materials",
  "equipment",
  "development",
  "offsets",
  "other",
];

/** Chart type dropdown options. */
export const CHART_TYPE_OPTIONS = [
  { value: "line", label: "Line" },
  { value: "bar", label: "Bar" },
  { value: "stackedBar", label: "Stacked Bar" },
  { value: "stackedBar100", label: "100% Stacked Bar" },
  { value: "area", label: "Area" },
  { value: "stackedArea", label: "Stacked Area" },
];

/** Pretty titles for known group keys. Falls back to Title Case. */
export const GROUP_TITLES = {
  positions: "Positions",
  salaries: "Salaries",
  salary: "Salaries",
  services: "Purchased Services",
  "purchased Services": "Purchased Services",
  materials: "Supplies / Materials",
  equipment: "Equipment",
  development: "Development",
  offsets: "Offsets",
  districts: "Districts",
  district: "District",
  elementary: "Elementary",
  revenue: "Revenue",
  revenues: "Revenues",
  taxLevy: "Tax Levy",
  motorVehicle: "Motor Vehicle",
  delinquentFees: "Delinquent / Fees",
  permits: "Permits",
  townClerk: "Town Clerk",
  planning: "Planning",
  publicWorks: "Public Works",
  parksRecreation: "Parks & Recreation",
  healthSafety: "Health & Safety",
  communityServices: "Community Services",
  education: "Education",
  purchasing: "Purchasing",
  publicSafety: "Public Safety",
  library: "Library",
  reimbursements: "Reimbursements",
  investmentIncome: "Investment Income",
  leaseProperty: "Leases / Property",
  miscellaneous: "Miscellaneous",
  spending: "Spending",
  budget: "Budget",
  budgets: "Budgets",
  totals: "Totals",
  rent: "Rent",
  income: "Income",
  housing: "Housing",
  fmr: "FMR",
  limits: "Limits",
  staffTotals: "Totals",
  staffAdministration: "Administration",
  staffCertified: "Certified Staff",
  staffInstructionalSupport: "Instructional Support",
  staffSubstitutes: "Substitutes",
  staffOperations: "Operations",
  staffClerical: "Clerical",
  staffOther: "Other Staff",
  other: "Other",
};

/**
 * Convert a hex or rgb color to an rgba() string with the specified alpha.
 *
 * Supports:
 * - "#rrggbb"
 * - "#rgb"
 * - "rgb(r,g,b)"
 *
 * If parsing fails, returns the original string to avoid hard failures.
 *
 * @param {string} color - A CSS color string.
 * @param {number} alpha - Opacity between 0 and 1.
 * @returns {string} rgba() color or original value if parsing fails.
 */
export function withAlpha(color, alpha) {
  if (!color) return color;

  // Hex input -> rgba(r,g,b,a)
  if (color.startsWith("#")) {
    let hex = color.slice(1);

    // Expand shorthand "#rgb" to "#rrggbb"
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }

    const int = parseInt(hex, 16);
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // rgb(...) -> rgba(..., a)
  if (color.startsWith("rgb(")) {
    return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
  }

  return color;
}

/**
 * Determine which series keys should be visible initially.
 *
 * Priority:
 *  1) defaultVisibleKeys (explicit)
 *  2) groups[] entries that specify defaultVisible (group mode)
 *  3) series[] entries that specify defaultVisible (series mode)
 *  4) fallback: show everything
 *
 * @param {ChartSeries[]} series - All series definitions.
 * @param {string[]} [defaultVisibleKeys] - Explicit list of series keys to enable initially.
 * @param {SeriesToggleGroup[]} [groups] - Optional explicit groups for grouped toggle mode.
 * @returns {string[]} Keys that should start enabled.
 */
export function getInitialVisibleKeys(series, defaultVisibleKeys, groups) {
  if (Array.isArray(defaultVisibleKeys) && defaultVisibleKeys.length > 0) {
    return defaultVisibleKeys;
  }

  const hasGroups = Array.isArray(groups) && groups.length > 0;

  // Group mode: allow defaultVisible to be declared at the group-toggle level
  if (hasGroups) {
    const groupsWithFlag = groups.filter(
      (g) => typeof g.defaultVisible === "boolean"
    );

    if (groupsWithFlag.length > 0) {
      const keySet = new Set();

      groupsWithFlag.forEach((g) => {
        if (g.defaultVisible && Array.isArray(g.seriesKeys)) {
          g.seriesKeys.forEach((k) => keySet.add(k));
        }
      });

      if (keySet.size > 0) return [...keySet];
    }
  }

  // Series mode: use per-series defaultVisible flags when present
  const withFlag = (Array.isArray(series) ? series : []).filter(
    (s) => typeof s?.defaultVisible === "boolean"
  );

  if (withFlag.length > 0) {
    return withFlag.filter((s) => s.defaultVisible).map((s) => s.key);
  }

  // Default: show everything
  return (Array.isArray(series) ? series : []).map((s) => s.key);
}

/**
 * Title-case fallback for group keys like "development" or "equipment".
 * Converts underscores/dashes into spaces and capitalizes each token.
 *
 * @param {string} [key=""]
 * @returns {string}
 */
export function titleCaseGroup(key = "") {
  const s = String(key || "").trim();
  if (!s) return "Other";

  return s
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

/**
 * If the label starts with "{Group} – " / "{Group} - " / "{Group} — ",
 * strip that prefix (case-insensitive) ONLY when it matches series.group.
 *
 * This improves toggle readability when labels are stored with a grouping prefix.
 *
 * @param {string} label - Original label (possibly containing a group prefix).
 * @param {string} groupKey - The series' group key.
 * @returns {string} Label with group prefix removed when applicable.
 */
export function stripGroupPrefix(label, groupKey) {
  const raw = String(label || "").trim();
  const g = String(groupKey || "")
    .trim()
    .toLowerCase();

  if (!raw || !g) return raw;

  // Allow hyphen, en dash, em dash, with optional surrounding spaces.
  const dashPattern = "\\s*[-–—]\\s*";
  const m = raw.match(new RegExp(`^(.+?)${dashPattern}(.*)$`));
  if (!m) return raw;

  const prefix = String(m[1] || "")
    .trim()
    .toLowerCase();
  const rest = String(m[2] || "").trim();

  if (prefix === g && rest) return rest;
  return raw;
}

/**
 * Auto-group series by series.group for toggle rendering when NOT in explicit groups mode.
 *
 * Output format:
 *   Array<[groupKey, ChartSeries[]]>
 *
 * Sorting:
 * - Within group: label ascending
 * - Group order: GROUP_ORDER first, then unknown groups
 *
 * @param {ChartSeries[]} [safeSeries=[]]
 * @param {boolean} [usingGroups=false] - When true, auto-grouping is disabled.
 * @returns {Array<[string, ChartSeries[]]>}
 */
export function buildAutoGroupedSeries(safeSeries = [], usingGroups = false) {
  if (usingGroups) return [];

  const map = new Map();

  (Array.isArray(safeSeries) ? safeSeries : [])
    .filter(
      (s) =>
        s?.key !== "__total__" &&
        s?.key !== "__TOTAL__" &&
        s?.key !== "Total" &&
        !s?._hideFromToggles,
    )
    .forEach((s) => {
    const g = s?.group || "other";
    if (!map.has(g)) map.set(g, []);
    map.get(g).push(s);
  });

  // Stable sorting within each group for consistent UI
  for (const list of map.values()) {
    list.sort((a, b) => String(a?.label).localeCompare(String(b?.label)));
  }

  // Sort groups by preferred order
  const entries = Array.from(map.entries());
  entries.sort((a, b) => {
    const ai = GROUP_ORDER.indexOf(a[0]);
    const bi = GROUP_ORDER.indexOf(b[0]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return entries;
}

/**
 * Group toggle definitions based on explicit `groups` prop.
 *
 * Output format:
 *   Array<[groupKey, SeriesToggleGroup[]]>
 *
 * This is used when the caller passes `groups` to LineChartWithToggles,
 * e.g. for “Actual vs Approved” views where one checkbox maps to multiple series.
 *
 * @param {SeriesToggleGroup[]} [groups=[]]
 * @param {boolean} [usingGroups=false] - When false, grouped toggles are disabled.
 * @returns {Array<[string, SeriesToggleGroup[]]>}
 */
export function buildManualGroupedToggles(groups = [], usingGroups = false) {
  if (!usingGroups) return [];

  const map = new Map();

  (Array.isArray(groups) ? groups : []).forEach((g) => {
    const key = g?.groupKey || "other";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(g);
  });

  // Stable sorting within each group for consistent UI
  for (const list of map.values()) {
    list.sort((a, b) => String(a?.label).localeCompare(String(b?.label)));
  }

  // Sort groups by preferred order
  const entries = Array.from(map.entries());
  entries.sort((a, b) => {
    const ai = GROUP_ORDER.indexOf(a[0]);
    const bi = GROUP_ORDER.indexOf(b[0]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return entries;
}

/**
 * Find and return an explicitly provided “Total” series (if present).
 *
 * This is intended for situations where the caller already computed a total series,
 * and wants the “none selected -> show total” fallback to use that series.
 *
 * Identification:
 * - key === "Total"
 * - key === "__total__"
 * - key === "__TOTAL__"
 *
 * Notes:
 * - Only used in non-group mode (group mode typically doesn't support “total fallback” cleanly).
 * - Normalizes color + label for consistent display.
 *
 * @param {ChartSeries[]} [safeSeries=[]]
 * @param {boolean} [usingGroups=false]
 * @param {string} [totalLabel="Total Budget"]
 * @returns {ChartSeries|null}
 */
export function buildProvidedTotalSeries(
  safeSeries = [],
  usingGroups = false,
  totalLabel = "Total Budget"
) {
  if (usingGroups) return null;

  const list = Array.isArray(safeSeries) ? safeSeries : [];
  const t =
    list.find((s) => s?.key === "Total") ||
    list.find((s) => s?.key === "__total__") ||
    list.find((s) => s?.key === "__TOTAL__");

  if (!t) return null;

  return {
    ...t,
    // Default “total” color (green) if not provided by caller
    color: t.color || "#16a34a",
    // Force the label to match the component’s configured total label
    label: totalLabel || t.label,
  };
}

/**
 * Compute a “Total Budget” series by summing all currency series at each index.
 *
 * This is a fallback when the caller did not provide a total series explicitly.
 *
 * Important behavior:
 * - Only sums series where (valueType || "currency") === "currency"
 * - Ignores non-numeric values
 * - If labels/series are missing, returns null to avoid misleading output
 *
 * @param {string[]} [labels=[]]
 * @param {ChartSeries[]} [safeSeries=[]]
 * @param {string} [totalLabel="Total Budget"]
 * @returns {ChartSeries|null}
 */
export function buildTotalBudgetSeries(
  labels = [],
  safeSeries = [],
  totalLabel = "Total Budget"
) {
  if (!Array.isArray(labels) || labels.length === 0) return null;
  if (!Array.isArray(safeSeries) || safeSeries.length === 0) return null;

  const currencySeries = safeSeries.filter(
    (s) => (s?.valueType || "currency") === "currency"
  );
  if (currencySeries.length === 0) return null;

  const totals = labels.map((_, idx) =>
    currencySeries.reduce((acc, s) => {
      const base = s?.data?.[idx];
      if (typeof base === "number" && Number.isFinite(base)) return acc + base;
      return acc;
    }, 0)
  );

  return {
    key: "__TOTAL_BUDGET__",
    label: totalLabel,
    data: totals,
    valueType: "currency",
    group: "other",
    color: "#16a34a",
  };
}

/**
 * Decide which “total” series should be used for “none selected -> show total”.
 *
 * Priority:
 *  1) providedTotalSeries (explicit)
 *  2) totalBudgetSeries (computed)
 *
 * @param {ChartSeries|null} providedTotalSeries
 * @param {ChartSeries|null} totalBudgetSeries
 * @returns {ChartSeries|null}
 */
export function getNoneSelectedTotalSeries(
  providedTotalSeries,
  totalBudgetSeries
) {
  return providedTotalSeries || totalBudgetSeries || null;
}

/**
 * Infer which scale/formatting mode should be used based on what is displayed.
 *
 * The returned mode drives:
 * - Tooltip formatting
 * - Y-axis tick formatting
 * - Y-axis title
 *
 * Rules:
 * - If none selected and total fallback is active, derive type from the chosen total series.
 * - Else, derive from the visible series types in priority order:
 *   currency > percent > count (first match wins)
 *
 * @param {Object} args
 * @param {ChartSeries[]} [args.safeSeries=[]]
 * @param {string[]} [args.visibleKeys=[]]
 * @param {boolean} [args.showTotalWhenNoneSelected=false]
 * @param {ChartSeries|null} [args.noneSelectedTotalSeries=null]
 * @returns {"currency"|"percent"|"count"}
 */
export function computeScaleMode({
  safeSeries = [],
  visibleKeys = [],
  showTotalWhenNoneSelected = false,
  noneSelectedTotalSeries = null,
}) {
  const list = Array.isArray(safeSeries) ? safeSeries : [];
  const keys = Array.isArray(visibleKeys) ? visibleKeys : [];

  const visible = list.filter((s) => keys.includes(s?.key));
  const noneSelected = visible.length === 0;

  // None-selected fallback: derive formatting from the displayed total series
  if (noneSelected && showTotalWhenNoneSelected && noneSelectedTotalSeries) {
    const vt = noneSelectedTotalSeries.valueType || "currency";
    if (vt === "count") return "count";
    if (vt === "percent") return "percent";
    return "currency";
  }

  // Conservative default
  if (visible.length === 0) return "currency";

  const hasCurrency = visible.some(
    (s) => (s?.valueType || "currency") === "currency"
  );
  const hasPercent = visible.some((s) => s?.valueType === "percent");
  const hasCount = visible.some((s) => s?.valueType === "count");

  if (hasCurrency) return "currency";
  if (hasPercent) return "percent";
  if (hasCount) return "count";
  return "currency";
}

/**
 * Build Chart.js dataset structure for Line/Bar (and Area variants).
 *
 * Responsibilities:
 * - Select visible series (or total fallback series if none selected)
 * - Apply chartType rules (bar vs line-like)
 * - Implement area/stackedArea fill behavior
 * - Render proposedData as either:
 *    - overlay dataset (dashed), OR
 *    - merge into one continuous dataset with dashed segment
 * - Inject helper datasets (shading) that are hidden from legend/tooltips
 *
 * Dataset metadata conventions used by buildLineChartOptions():
 * - _isHelper: true => hidden from legend + tooltip
 * - _isProposedFallback: true => hidden from legend but visible in tooltip w/ "(Proposed)"
 * - _approvedLabel / _proposedLabel / _proposedOnlyIdxs: enable “label switching” in tooltip
 *
 * NOTE (2026-02): We now also attach `key` to datasets. Chart.js ignores unknown
 * fields, but our x-axis trimming logic needs a stable identifier that matches
 * `visibleKeys` (series keys). This preserves all existing behavior.
 *
 * @param {Object} args
 * @param {string[]} args.labels
 * @param {ChartSeries[]} args.safeSeries
 * @param {string[]} args.visibleKeys
 * @param {ChartType} args.chartType
 * @param {boolean} args.showTotalWhenNoneSelected
 * @param {ChartSeries|null} args.noneSelectedTotalSeries
 * @param {string} args.totalLabel
 * @param {boolean} args.usingGroups
 * @param {ProposedRenderMode} args.proposedRenderMode
 * @returns {{labels: string[], datasets: Array<Object>}}
 */
export function buildLineChartData({
  labels,
  safeSeries,
  visibleKeys,
  chartType,
  showTotalWhenNoneSelected,
  noneSelectedTotalSeries,
  totalLabel,
  usingGroups,
  proposedRenderMode,
  spanGaps = false,
}) {
  const safeLabels = Array.isArray(labels) ? labels : [];
  const seriesList = Array.isArray(safeSeries) ? safeSeries : [];
  const keys = Array.isArray(visibleKeys) ? visibleKeys : [];

  const visibleSeries = seriesList.filter((s) => keys.includes(s?.key));
  const noneSelected = visibleSeries.length === 0;

  const isBar =
    chartType === "bar" ||
    chartType === "stackedBar" ||
    chartType === "stackedBar100";

  const isStackedBar =
    chartType === "stackedBar" || chartType === "stackedBar100";

  const isStackedBar100 = chartType === "stackedBar100";
  const isArea = chartType === "area";
  const isStackedArea = chartType === "stackedArea";
  const isLineLike = !isBar; // line / area / stackedArea

  const isFiniteNum = (v) => typeof v === "number" && Number.isFinite(v);

  // Detect any proposed values across all series (used by “total fallback with proposed” special-case)
  const hasAnyProposed = seriesList.some(
    (s) =>
      Array.isArray(s?.proposedData) &&
      s.proposedData.some((v) => isFiniteNum(v)),
  );

  /**
   * SPECIAL CASE (Total fallback + Proposed exists) — line-like charts only
   *
   * Prefer merging a provided total series if it contains proposedData.
   * Otherwise compute approved/proposed totals by summing currency series.
   */
  if (
    !usingGroups &&
    noneSelected &&
    showTotalWhenNoneSelected &&
    safeLabels.length > 0 &&
    hasAnyProposed &&
    !isBar
  ) {
    const totalHasProposed =
      noneSelectedTotalSeries &&
      Array.isArray(noneSelectedTotalSeries?.proposedData) &&
      noneSelectedTotalSeries.proposedData.some((v) => isFiniteNum(v));

    // --- 1) Provided total with proposedData: merge directly ---
    if (totalHasProposed) {
      const approvedArr = Array.isArray(noneSelectedTotalSeries?.data)
        ? noneSelectedTotalSeries.data
        : [];
      const proposedArr = Array.isArray(noneSelectedTotalSeries?.proposedData)
        ? noneSelectedTotalSeries.proposedData
        : [];

      const proposedOnlyIdxs = safeLabels
        .map((_, i) => {
          const aOk = isFiniteNum(approvedArr[i]);
          const pOk = isFiniteNum(proposedArr[i]);
          return !aOk && pOk ? i : null;
        })
        .filter((x) => x !== null);

      const combinedTotals = safeLabels.map((_, i) => {
        if (isFiniteNum(approvedArr[i])) return approvedArr[i];
        if (isFiniteNum(proposedArr[i])) return proposedArr[i];
        return null;
      });

      const lastProposedIdx =
        proposedOnlyIdxs.length > 0
          ? proposedOnlyIdxs[proposedOnlyIdxs.length - 1]
          : -1;

      let lastApprovedIdx = -1;
      if (lastProposedIdx > 0) {
        for (let i = lastProposedIdx - 1; i >= 0; i--) {
          if (isFiniteNum(approvedArr[i])) {
            lastApprovedIdx = i;
            break;
          }
        }
      }

      const approvedLabel = totalLabel;
      const proposedLabel =
        typeof totalLabel === "string" && /approved/i.test(totalLabel)
          ? totalLabel.replace(/approved/gi, "Proposed")
          : `${totalLabel} (Proposed)`;

      const color =
        noneSelectedTotalSeries?.color || DEFAULT_COLORS[0] || "#16a34a";
      const valueType = noneSelectedTotalSeries?.valueType || "currency";

      const datasets = [];

      datasets.push({
        // ✅ stable identifier for trimming/toggles
        key: "__TOTAL_COMBINED__",

        label: approvedLabel,
        data: combinedTotals,
        fill: isArea || isStackedArea ? "origin" : false,
        tension: 0.25,
        borderWidth: 3,
        borderColor: color,
        backgroundColor:
          isArea || isStackedArea ? withAlpha(color, 0.22) : color,

        valueType,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 8,

        _seriesKey: "__TOTAL_COMBINED__",
        _proposedOnlyIdxs: proposedOnlyIdxs,
        _approvedLabel: approvedLabel,
        _proposedLabel: proposedLabel,

        segment: {
          borderDash: (ctx) => {
            const p1 = ctx?.p1DataIndex;
            return Array.isArray(ctx?.dataset?._proposedOnlyIdxs) &&
              ctx.dataset._proposedOnlyIdxs.includes(p1)
              ? [6, 4]
              : undefined;
          },
          borderWidth: (ctx) => {
            const p1 = ctx?.p1DataIndex;
            return Array.isArray(ctx?.dataset?._proposedOnlyIdxs) &&
              ctx.dataset._proposedOnlyIdxs.includes(p1)
              ? 4
              : 3;
          },
        },

        order: 2,
        spanGaps,
      });

      // Optional shading helper ONLY for plain line charts
      if (
        chartType === "line" &&
        lastApprovedIdx >= 0 &&
        lastProposedIdx > lastApprovedIdx
      ) {
        const shadeData = safeLabels.map(() => null);
        shadeData[lastApprovedIdx] = combinedTotals[lastApprovedIdx];
        shadeData[lastProposedIdx] = combinedTotals[lastProposedIdx];

        datasets.push({
          label: "__proposed_fill__",
          data: shadeData,
          fill: "origin",
          tension: 0.25,
          borderWidth: 0,
          borderColor: "transparent",
          backgroundColor: withAlpha(color, 0.18),
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 0,
          _isHelper: true,
          order: 1,
          spanGaps,
        });
      }

      return { labels: safeLabels, datasets };
    }

    // --- 2) Otherwise: currency-summed totals merge (existing behavior) ---
    const currencySeries = seriesList.filter(
      (s) => (s?.valueType || "currency") === "currency",
    );

    const approvedTotals = safeLabels.map((_, idx) => {
      let acc = 0;
      let saw = false;

      for (const s of currencySeries) {
        const a = s?.data?.[idx];
        if (isFiniteNum(a)) {
          acc += a;
          saw = true;
        }
      }

      return saw ? acc : null;
    });

    const proposedTotals = safeLabels.map((_, idx) => {
      let acc = 0;
      let saw = false;

      for (const s of currencySeries) {
        const p = s?.proposedData?.[idx];
        if (isFiniteNum(p)) {
          acc += p;
          saw = true;
        }
      }

      return saw ? acc : null;
    });

    const proposedOnlyIdxs = proposedTotals
      .map((v, idx) =>
        isFiniteNum(v) && approvedTotals[idx] == null ? idx : null,
      )
      .filter((x) => x !== null);

    const combinedTotals = safeLabels.map((_, idx) => {
      if (isFiniteNum(approvedTotals[idx])) return approvedTotals[idx];
      if (isFiniteNum(proposedTotals[idx])) return proposedTotals[idx];
      return null;
    });

    const lastProposedIdx =
      proposedOnlyIdxs.length > 0
        ? proposedOnlyIdxs[proposedOnlyIdxs.length - 1]
        : -1;

    let lastApprovedIdx = -1;
    if (lastProposedIdx > 0) {
      for (let i = lastProposedIdx - 1; i >= 0; i--) {
        if (isFiniteNum(approvedTotals[i])) {
          lastApprovedIdx = i;
          break;
        }
      }
    }

    const approvedLabel = totalLabel;
    const proposedLabel =
      typeof totalLabel === "string" && /approved/i.test(totalLabel)
        ? totalLabel.replace(/approved/gi, "Proposed")
        : "Total Proposed Budget";

    const color =
      noneSelectedTotalSeries?.color || DEFAULT_COLORS[0] || "#16a34a";

    const datasets = [];

    datasets.push({
      // ✅ stable identifier for trimming/toggles
      key: "__TOTAL_COMBINED__",

      label: approvedLabel,
      data: combinedTotals,
      fill: isArea || isStackedArea ? "origin" : false,
      tension: 0.25,
      borderWidth: 3,
      borderColor: color,
      backgroundColor:
        isArea || isStackedArea ? withAlpha(color, 0.22) : color,

      valueType: "currency",
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHitRadius: 8,

      _seriesKey: "__TOTAL_COMBINED__",
      _proposedOnlyIdxs: proposedOnlyIdxs,
      _approvedLabel: approvedLabel,
      _proposedLabel: proposedLabel,

      segment: {
        borderDash: (ctx) => {
          const p1 = ctx?.p1DataIndex;
          return Array.isArray(ctx?.dataset?._proposedOnlyIdxs) &&
            ctx.dataset._proposedOnlyIdxs.includes(p1)
            ? [6, 4]
            : undefined;
        },
        borderWidth: (ctx) => {
          const p1 = ctx?.p1DataIndex;
          return Array.isArray(ctx?.dataset?._proposedOnlyIdxs) &&
            ctx.dataset._proposedOnlyIdxs.includes(p1)
            ? 4
            : 3;
        },
      },

      order: 2,
      spanGaps,
    });

    if (
      chartType === "line" &&
      lastApprovedIdx >= 0 &&
      lastProposedIdx > lastApprovedIdx
    ) {
      const shadeData = safeLabels.map(() => null);
      shadeData[lastApprovedIdx] = combinedTotals[lastApprovedIdx];
      shadeData[lastProposedIdx] = combinedTotals[lastProposedIdx];

      datasets.push({
        label: "__proposed_fill__",
        data: shadeData,
        fill: "origin",
        tension: 0.25,
        borderWidth: 0,
        borderColor: "transparent",
        backgroundColor: withAlpha(color, 0.18),
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        _isHelper: true,
        order: 1,
        spanGaps,
      });
    }

    return { labels: safeLabels, datasets };
  }

  // ---------- Default behavior ----------
  // If none selected and total fallback is enabled, show only the total series; otherwise show visible series.
  const effectiveSeries =
    noneSelected && showTotalWhenNoneSelected && noneSelectedTotalSeries
      ? [noneSelectedTotalSeries]
      : visibleSeries;

  const datasets = [];

  for (let idx = 0; idx < effectiveSeries.length; idx++) {
    const s = effectiveSeries[idx];

    const color = s?.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
    const valueType = s?.valueType || "currency";

    const displayLabel = stripGroupPrefix(s?.label, s?.group);

    const hasProposed =
      Array.isArray(s?.proposedData) &&
      s.proposedData.some((v) => isFiniteNum(v));

    /**
     * MERGE MODE (line-like only)
     */
    if (
      !usingGroups &&
      proposedRenderMode === "merge" &&
      isLineLike &&
      hasProposed
    ) {
      const approvedArr = Array.isArray(s?.data) ? s.data : [];
      const proposedArr = Array.isArray(s?.proposedData) ? s.proposedData : [];

      const proposedOnlyIdxs = safeLabels
        .map((_, i) => {
          const aOk = isFiniteNum(approvedArr[i]);
          const pOk = isFiniteNum(proposedArr[i]);
          return !aOk && pOk ? i : null;
        })
        .filter((x) => x !== null);

      const combined = safeLabels.map((_, i) => {
        if (isFiniteNum(approvedArr[i])) return approvedArr[i];
        if (isFiniteNum(proposedArr[i])) return proposedArr[i];
        return null;
      });

      const lastProposedIdx =
        proposedOnlyIdxs.length > 0
          ? proposedOnlyIdxs[proposedOnlyIdxs.length - 1]
          : -1;

      let lastApprovedIdx = -1;
      if (lastProposedIdx > 0) {
        for (let i = lastProposedIdx - 1; i >= 0; i--) {
          if (isFiniteNum(approvedArr[i])) {
            lastApprovedIdx = i;
            break;
          }
        }
      }

      const approvedLabel = displayLabel;
      const proposedLabel =
        typeof displayLabel === "string" && /approved/i.test(displayLabel)
          ? displayLabel.replace(/approved/gi, "Proposed")
          : `${displayLabel} (Proposed)`;

      datasets.push({
        // ✅ stable identifier for trimming/toggles (match visibleKeys)
        key: s?.key,

        label: approvedLabel,
        data: combined,
        fill: isArea || isStackedArea ? "origin" : false,
        tension: 0.25,
        borderWidth: 3,
        borderColor: color,
        backgroundColor:
          isArea || isStackedArea ? withAlpha(color, 0.22) : color,

        valueType,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 8,

        _seriesKey: `${s?.key}__MERGED__`,
        _proposedOnlyIdxs: proposedOnlyIdxs,
        _approvedLabel: approvedLabel,
        _proposedLabel: proposedLabel,

        segment: {
          borderDash: (ctx) => {
            const p1 = ctx?.p1DataIndex;
            return Array.isArray(ctx?.dataset?._proposedOnlyIdxs) &&
              ctx.dataset._proposedOnlyIdxs.includes(p1)
              ? [6, 4]
              : undefined;
          },
          borderWidth: (ctx) => {
            const p1 = ctx?.p1DataIndex;
            return Array.isArray(ctx?.dataset?._proposedOnlyIdxs) &&
              ctx.dataset._proposedOnlyIdxs.includes(p1)
              ? 4
              : 3;
          },
        },

        _isHelper: false,
        _isProposedFallback: false,
        order: 2,
        spanGaps: false,
      });

      // Optional shading helper ONLY for plain line charts
      if (
        chartType === "line" &&
        lastApprovedIdx >= 0 &&
        lastProposedIdx > lastApprovedIdx
      ) {
        const shadeData = safeLabels.map(() => null);
        shadeData[lastApprovedIdx] = combined[lastApprovedIdx];
        shadeData[lastProposedIdx] = combined[lastProposedIdx];

        datasets.push({
          label: `__fill_${s?.key}__`,
          data: shadeData,
          fill: "origin",
          tension: 0.25,
          borderWidth: 0,
          borderColor: "transparent",
          backgroundColor: withAlpha(color, 0.18),
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 0,
          _isHelper: true,
          _isProposedFallback: false,
          order: 1,
          spanGaps: false,
        });
      }

      continue; // do not fall through to overlay mode
    }

    /**
     * Base dataset
     */
    datasets.push({
      // ✅ stable identifier for trimming/toggles
      key: s?.key,

      label: displayLabel,
      data: Array.isArray(s?.data) ? s.data : [],

      fill: isStackedArea
        ? idx === 0
          ? "origin"
          : "-1"
        : isArea
          ? "origin"
          : false,

      tension: isLineLike ? 0.25 : 0,
      borderWidth: isLineLike ? 3 : 1,
      borderColor: color,
      backgroundColor: isArea || isStackedArea ? withAlpha(color, 0.22) : color,

      // Stack IDs
      stack: isStackedArea
        ? "__stackedArea__"
        : isStackedBar
          ? isStackedBar100
            ? "__stackedBar100__"
            : "__stackedBar__"
          : undefined,

      // Don’t reserve bar “slots” for nulls
      skipNull: isBar ? true : undefined,

      valueType,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHitRadius: 8,

      _isHelper: false,
      _isProposedFallback: false,
      _seriesKey: s?.key,
      order: 2,
      spanGaps,
    });

    /**
     * Proposed overlay dataset (dashed)
     *
     * IMPORTANT: In stackedBar100, do NOT render overlay datasets at all.
     * Rendering proposed overlays in 100% mode creates a second stack and can exceed 100%.
     */
    const allowProposedOverlay = !isStackedBar100;

    if (allowProposedOverlay && Array.isArray(s?.proposedData)) {
      const hasAny = s.proposedData.some((v) => isFiniteNum(v));
      if (hasAny) {
        datasets.push({
          // ✅ stable identifier for trimming/toggles
          key: s?.key,

          label: displayLabel,
          data: s.proposedData,

          borderWidth: isBar ? 1 : isLineLike ? 4 : 1,
          borderDash: isLineLike ? [6, 4] : undefined,
          borderColor: color,

          backgroundColor: isLineLike
            ? withAlpha(color, isArea ? 0.14 : 0.28)
            : withAlpha(color, 0.45),

          // For stackedBar (non-100%), proposed lives in its own stack so it doesn't distort approved totals
          stack: isStackedBar ? "__proposedStack__" : undefined,

          skipNull: isBar ? true : undefined,

          valueType,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHitRadius: 8,

          _isProposedFallback: true,
          _isHelper: false,
          _seriesKey: s?.key,
          order: 3,
          spanGaps,
        });
      }
    }
  }

  // 100% stacked bar: normalize each x-index to percent-of-total (single stack only)
  // 100% stacked bar: normalize per index.
  // IMPORTANT: handle negatives correctly by normalizing positives and negatives separately.
  if (isStackedBar100) {
    const n = safeLabels.length;

    // Totals for positive and negative contributions per column
    const posTotals = Array.from({ length: n }, () => 0);
    const negTotalsAbs = Array.from({ length: n }, () => 0);

    // 1) Compute denominators using ONLY base datasets (no helpers)
    for (const ds of datasets) {
      if (ds?._isHelper) continue;

      const arr = Array.isArray(ds.data) ? ds.data : [];
      for (let i = 0; i < n; i++) {
        const v = arr[i];
        if (!isFiniteNum(v)) continue;

        if (v >= 0) posTotals[i] += v;
        else negTotalsAbs[i] += Math.abs(v);
      }
    }

    // 2) Normalize each dataset’s values into +/- percentages
    for (const ds of datasets) {
      if (ds?._isHelper) continue;

      const arr = Array.isArray(ds.data) ? ds.data : [];

      ds.data = arr.map((v, i) => {
        if (!isFiniteNum(v)) return null;

        if (v >= 0) {
          const denom = posTotals[i];
          if (!isFiniteNum(denom) || denom === 0) return 0;
          return (v / denom) * 100;
        }

        // negative side: return negative percentage
        const denomNeg = negTotalsAbs[i];
        if (!isFiniteNum(denomNeg) || denomNeg === 0) return 0;
        return -((Math.abs(v) / denomNeg) * 100);
      });

      ds.valueType = "percent";

      // Separate stacks for positive and negative so Chart.js doesn't mix them.
      // This also keeps each side bounded to 100%.
      ds.stack = "__stackedBar100_posneg__";
    }
  }

  return { labels: safeLabels, datasets };
}

/**
 * Build Chart.js options object used by LineChartWithToggles.
 *
 * Key behavior:
 * - Legend filters out helper datasets and proposed overlays to keep legend concise.
 * - Tooltip formatting adapts to currency/count/percent modes.
 * - When using merged proposed lines, tooltip label switches between approved/proposed labels
 *   based on data index (via _proposedOnlyIdxs metadata).
 *
 * @param {Object} args
 * @param {"currency"|"percent"|"count"} args.scaleMode - Determines tick + tooltip formatting.
 * @param {ChartType} args.chartType - Enables stacking behavior for stackedArea/stackedBar.
 * @returns {import("chart.js").ChartOptions}
 */
export function buildLineChartOptions({ scaleMode, chartType }) {
  const isStackedBar100 = chartType === "stackedBar100";
  const stacked =
    chartType === "stackedArea" ||
    chartType === "stackedBar" ||
    isStackedBar100;

  return {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        labels: {
          /**
           * Hide helper datasets (shading) and proposed overlay datasets
           * so the legend remains “one item per logical series.”
           */
          filter: (legendItem, data) => {
            const ds = data?.datasets?.[legendItem.datasetIndex];
            return !ds?._isHelper && !ds?._isProposedFallback;
          },
        },
      },
      tooltip: {
        callbacks: {
          /**
           * Tooltip label formatting.
           *
           * Notes:
           * - Helper datasets return empty string to avoid junk tooltip rows.
           * - Proposed overlay datasets append " (Proposed)".
           * - Merge-mode datasets can switch labelName at proposed-only points.
           */
          label: (ctx) => {
            const ds = ctx.dataset;
            if (ds?._isHelper) return "";

            // Label switching for merge-mode datasets
            let labelName = ds?.label || "Value";
            if (
              Array.isArray(ds?._proposedOnlyIdxs) &&
              ds._proposedOnlyIdxs.includes(ctx.dataIndex)
            ) {
              labelName = ds._proposedLabel || labelName;
            } else if (ds?._approvedLabel) {
              labelName = ds._approvedLabel;
            }

            const raw = Number(ctx.raw ?? 0);

            // Prefer dataset-defined valueType; fall back to scaleMode
            const type =
              ds?.valueType || (scaleMode === "count" ? "count" : "currency");

            const formatted =
              type === "currency"
                ? `$${raw.toLocaleString("en-US")}`
                : type === "percent"
                ? `${raw.toFixed(2)}%`
                : raw.toLocaleString("en-US");

            const proposedTag = ds?._isProposedFallback ? " (Proposed)" : "";
            return `${labelName}${proposedTag}: ${formatted}`;
          },
        },
      },
    },
    scales: {
      y: {
        stacked,
        beginAtZero: true,
        ...(isStackedBar100 ? { min: -100, max: 100 } : null),
        ticks: {
          callback: (v) => {
            const num = Number(v);

            // Force percent formatting for 100% stacked bars
            if (isStackedBar100) return `${num}%`;

            if (scaleMode === "currency")
              return `$${num.toLocaleString("en-US")}`;
            if (scaleMode === "percent") return `${num}%`;
            return num.toLocaleString("en-US");
          },
        },
        title: {
          display: true,
          text: isStackedBar100
            ? "% of Total"
            : scaleMode === "currency"
            ? "Budget"
            : scaleMode === "percent"
            ? "% of Total"
            : "Positions / Counts",
        },
      },
      x: {
        stacked,
        title: { display: true, text: "Fiscal Year" },
      },
    },
  };
}
